import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuditTrail } from '@/components/AuditTrail';
import {
  Badge,
  Callout,
  Card,
  DefinitionList,
  Field,
  SectionHeading,
  TextArea,
  TextInput,
  type ToneName,
} from '@/components/ui';
import { formatDate, formatDateTime, todayIso } from '@/domain/dates';
import { formatEur, lineNetCents, parseAmountToCents, summariseVat } from '@/domain/money';
import type { EInvoiceFormat, Invoice } from '@/domain/types';
import { invoiceRepo } from '@/db/repositories';
import { downloadBlob } from '@/export/zip';
import { useSession } from '@/session/SessionContext';
import { useT } from '@/i18n';
import { de } from '@/i18n/de';
import {
  E_INVOICE_FORMAT_LABELS,
  EInvoiceIncompleteError,
  checkEInvoiceReadiness,
  generateEInvoice,
} from './einvoice';
import {
  cancelInvoice,
  dunningLabel,
  isOverdue,
  markPaid,
  markUnpaid,
  raiseDunningLevel,
} from './invoiceService';

const STATUS_TONES: Record<Invoice['status'], ToneName> = {
  entwurf: 'neutral',
  offen: 'info',
  bezahlt: 'success',
  storniert: 'neutral',
  ueberfaellig: 'danger',
};

export function InvoiceDetailScreen() {
  const session = useSession();
  const t = useT();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();

  const [invoice, setInvoice] = useState<Invoice | null | 'missing'>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [paymentInput, setPaymentInput] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => {
    void (async () => {
      if (!invoiceId) return;
      const found = await invoiceRepo.get(session.dataKey, invoiceId);
      setInvoice(found ?? 'missing');
      if (found) setPaymentInput((found.grossTotalCents / 100).toFixed(2).replace('.', ','));
    })();
  }, [session.dataKey, invoiceId]);

  if (invoice === null) return <p className="text-muted text-sm">{t.common.loading}</p>;
  if (invoice === 'missing') {
    return (
      <Callout tone="danger" title={t.invoices.notFound}>
        <Link to="/rechnungen" className="underline">
          {t.invoices.backToList}
        </Link>
      </Callout>
    );
  }

  const overdue = isOverdue(invoice, todayIso());
  const readiness = checkEInvoiceReadiness(invoice, session.company);
  const buckets = summariseVat(
    invoice.lines.map((line) => ({
      netCents: lineNetCents(line.quantity, line.unitPriceNetCents),
      vatRatePercent: line.vatRatePercent,
    })),
  );

  async function guard(key: string, work: () => Promise<void>) {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (cause) {
      if (cause instanceof EInvoiceIncompleteError) {
        setError(cause.problems.join(' '));
      } else {
        setError(cause instanceof Error ? cause.message : t.invoices.errGeneric);
      }
    } finally {
      setBusy(null);
    }
  }

  const current = invoice;

  async function downloadPdf(asDunning: boolean) {
    await guard(asDunning ? 'mahnung' : 'pdf', async () => {
      const { renderInvoicePdf } = await import('./invoicePdf');
      const blob = await renderInvoicePdf(current, session.company, { asDunning });
      const prefix = asDunning ? 'Mahnung' : 'Rechnung';
      downloadBlob(blob, `${prefix}-${current.invoiceNumber || 'Entwurf'}.pdf`);
    });
  }

  async function downloadEInvoice(format: Exclude<EInvoiceFormat, 'keine'>) {
    await guard(`e-${format}`, async () => {
      const result = await generateEInvoice(current, session.company, format);
      downloadBlob(result.blob, result.filename);
      const updated = await invoiceRepo.update(
        session.audit,
        current.id,
        { eInvoiceFormat: format, eInvoiceGeneratedAt: new Date().toISOString() },
        { reason: de.invoices.eInvoiceReason(E_INVOICE_FORMAT_LABELS[format]) },
      );
      setInvoice(updated);
      setNotice(t.invoices.eInvoiceGenerated(E_INVOICE_FORMAT_LABELS[format]));
    });
  }

  return (
    <div className="space-y-5">
      <SectionHeading
        title={current.invoiceNumber || t.invoices.detailDraftTitle}
        description={`${current.customerName || t.invoices.noCustomer} · ${formatDate(current.issueDate)}`}
        action={
          <Link to="/rechnungen" className="btn-ghost">
            {t.common.back}
          </Link>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={overdue ? 'danger' : STATUS_TONES[current.status]}>
          {overdue && current.status !== 'ueberfaellig'
            ? t.labels.invoiceStatus.ueberfaellig
            : t.labels.invoiceStatus[current.status]}
        </Badge>
        {current.isKleinunternehmer && <Badge>§ 19 UStG</Badge>}
        {current.isKleinbetrag && <Badge>{t.invoices.kleinbetragFull}</Badge>}
        {current.dunningLevel > 0 && (
          <Badge tone="warning">{dunningLabel(current.dunningLevel, t)}</Badge>
        )}
        {current.cancelsInvoiceId && <Badge tone="danger">{t.invoices.stornoBadge}</Badge>}
        {current.eInvoiceGeneratedAt && (
          <Badge tone="info">{E_INVOICE_FORMAT_LABELS[current.eInvoiceFormat]}</Badge>
        )}
      </div>

      {error && <Callout tone="danger">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      {overdue && current.status !== 'storniert' && (
        <Callout tone="warning" title={t.invoices.overdueTitle}>
          {t.invoices.overdueBody(formatDate(current.dueDate))}{' '}
          {current.lastDunnedAt
            ? t.invoices.lastDunned(formatDateTime(current.lastDunnedAt))
            : t.invoices.neverDunned}
        </Callout>
      )}

      <Card title={t.invoices.positionsTitle}>
        <ul className="divide-line divide-y">
          {current.lines.map((line, index) => (
            <li key={line.id} className="flex items-start justify-between gap-4 py-2.5">
              <div className="min-w-0">
                <p className="text-muted text-xs">{t.invoices.position(index + 1)}</p>
                <p className="text-heading mt-0.5 text-sm whitespace-pre-line">
                  {line.description}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-heading text-sm">
                  {formatEur(lineNetCents(line.quantity, line.unitPriceNetCents))}
                </p>
                <p className="text-muted text-xs">
                  {current.isKleinunternehmer
                    ? t.invoices.withoutVat
                    : t.invoices.withVat(line.vatRatePercent)}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <div className="border-line mt-4 space-y-1 border-t pt-4 text-sm">
          <Row label={t.invoices.netAmount} value={formatEur(current.netTotalCents)} />
          {!current.isKleinunternehmer &&
            buckets.map((bucket) => (
              <Row
                key={bucket.ratePercent}
                label={t.invoices.vatOn(bucket.ratePercent, formatEur(bucket.netCents))}
                value={formatEur(bucket.vatCents)}
              />
            ))}
          <Row label={t.invoices.invoiceTotal} value={formatEur(current.grossTotalCents)} strong />
        </div>
      </Card>

      <Card title={t.invoices.details}>
        <DefinitionList
          items={[
            {
              term: t.invoices.invoiceNumber,
              value: current.invoiceNumber || t.invoices.notAssigned,
            },
            { term: t.invoices.numberCircle, value: current.numberCircle || t.common.dash },
            { term: t.invoices.issueDate, value: formatDate(current.issueDate) },
            {
              term: t.invoices.servicePeriod,
              value:
                current.serviceDateFrom === current.serviceDateTo
                  ? formatDate(current.serviceDateFrom)
                  : `${formatDate(current.serviceDateFrom)} – ${formatDate(current.serviceDateTo)}`,
            },
            { term: t.invoices.dueDate, value: formatDate(current.dueDate) },
            { term: t.invoices.recipientTerm, value: current.customerName || t.common.dash },
            {
              term: t.invoices.addressTerm,
              value: (
                <span className="whitespace-pre-line">
                  {current.customerAddress || t.common.dash}
                </span>
              ),
            },
            ...(current.customerVatId
              ? [{ term: t.invoices.customerVatTerm, value: current.customerVatId }]
              : []),
            ...(current.paidAt
              ? [
                  {
                    term: t.invoices.paidTerm,
                    value: `${formatDateTime(current.paidAt)}${
                      current.paidAmountCents !== undefined
                        ? ` · ${formatEur(current.paidAmountCents)}`
                        : ''
                    }`,
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Card title={t.invoices.documents}>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void downloadPdf(false)}
            disabled={busy !== null}
          >
            {busy === 'pdf' ? t.invoices.generating : t.invoices.invoiceAsPdf}
          </button>
          {current.dunningLevel > 0 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void downloadPdf(true)}
              disabled={busy !== null}
            >
              {busy === 'mahnung'
                ? t.invoices.generating
                : t.invoices.dunningAsPdf(dunningLabel(current.dunningLevel, t))}
            </button>
          )}
        </div>

        <div className="border-line mt-4 border-t pt-4">
          <p className="text-body text-sm font-medium">{t.invoices.eInvoice}</p>
          <p className="text-muted mt-1 text-xs">{t.invoices.eInvoiceNote}</p>

          {readiness.length > 0 ? (
            <Callout tone="warning" title={t.invoices.eInvoiceNotReady}>
              <ul className="mt-1 list-disc pl-5">
                {readiness.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </Callout>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {(['facturx', 'xrechnung-ubl', 'xrechnung-cii'] as const).map((format) => (
                <button
                  key={format}
                  type="button"
                  className="btn-secondary"
                  onClick={() => void downloadEInvoice(format)}
                  disabled={busy !== null}
                >
                  {busy === `e-${format}`
                    ? t.invoices.generating
                    : E_INVOICE_FORMAT_LABELS[format]}
                </button>
              ))}
            </div>
          )}

          {current.eInvoiceGeneratedAt && (
            <p className="text-muted mt-3 text-xs">
              {t.invoices.eInvoiceLast(formatDateTime(current.eInvoiceGeneratedAt))}
            </p>
          )}
        </div>
      </Card>

      {current.status === 'entwurf' ? (
        <Card title={t.invoices.draftTitle}>
          <p className="text-muted text-sm">{t.invoices.draftBody}</p>
          <Link to={`/rechnungen/${current.id}/bearbeiten`} className="btn-primary mt-3 inline-block">
            {t.invoices.editDraft}
          </Link>
        </Card>
      ) : (
        current.status !== 'storniert' && (
          <Card title={t.invoices.paymentAndDunning}>
            {current.status === 'bezahlt' ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-body text-sm">
                  {current.paidAt
                    ? t.invoices.paidOn(formatDateTime(current.paidAt))
                    : t.invoices.paidNoDate}
                </p>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() =>
                    void guard('unpaid', async () => {
                      setInvoice(await markUnpaid(session.audit, current.id));
                    })
                  }
                  disabled={busy !== null}
                >
                  {t.invoices.undo}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-40">
                  <Field label={t.invoices.paymentReceived}>
                    <TextInput
                      value={paymentInput}
                      onChange={(event) => setPaymentInput(event.target.value)}
                      inputMode="decimal"
                      className="text-right"
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() =>
                    void guard('paid', async () => {
                      const cents = parseAmountToCents(paymentInput);
                      if (cents === null) throw new Error(t.invoices.errAmount);
                      setInvoice(await markPaid(session.audit, current.id, cents));
                    })
                  }
                  disabled={busy !== null}
                >
                  {busy === 'paid' ? t.invoices.recording : t.invoices.markPaid}
                </button>
                {current.dunningLevel < 3 && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      void guard('dun', async () => {
                        setInvoice(await raiseDunningLevel(session.audit, current));
                      })
                    }
                    disabled={busy !== null}
                  >
                    {busy === 'dun'
                      ? t.invoices.creating
                      : t.invoices.createDunning(
                          dunningLabel((current.dunningLevel + 1) as 1 | 2 | 3, t),
                        )}
                  </button>
                )}
              </div>
            )}

            <div className="border-line mt-4 border-t pt-4">
              {showCancel ? (
                <div className="space-y-3">
                  <Field
                    label={t.invoices.cancelReason}
                    required
                    hint={t.invoices.cancelReasonHint}
                  >
                    <TextArea
                      rows={2}
                      value={cancelReason}
                      onChange={(event) => setCancelReason(event.target.value)}
                    />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() =>
                        void guard('cancel', async () => {
                          if (!cancelReason.trim()) {
                            throw new Error(t.invoices.errCancelReason);
                          }
                          const { storno } = await cancelInvoice(
                            session.audit,
                            current,
                            session.company,
                            cancelReason.trim(),
                          );
                          navigate(`/rechnungen/${storno.id}`);
                        })
                      }
                      disabled={busy !== null}
                    >
                      {busy === 'cancel' ? t.invoices.cancelling : t.invoices.createStorno}
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => setShowCancel(false)}>
                      {t.common.cancel}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-muted text-xs">{t.invoices.cancelNote}</p>
                  <button
                    type="button"
                    className="btn-ghost mt-2 text-xs text-red-600"
                    onClick={() => setShowCancel(true)}
                  >
                    {t.invoices.cancelInvoice}
                  </button>
                </>
              )}
            </div>
          </Card>
        )
      )}

      <Card title={t.invoices.history}>
        <AuditTrail entityId={current.id} />
      </Card>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={strong ? 'text-heading font-semibold' : 'text-muted'}>{label}</span>
      <span className={strong ? 'text-heading font-semibold' : 'text-body'}>{value}</span>
    </div>
  );
}
